import i18nFactory, { i18nRaw as i18nRawFactory } from '@yandex-int/i18n';
import classNames from 'classnames';
import { Select } from 'lego-on-react';
import * as React from 'react';

import { mapStringToCurrencySymbol } from '../../mappers/common/common';
import { Bem } from '../../service/Bem/Bem';
import {
    formatNumber,
    parseNumber
} from '../../service/FormatHelpers/FormatHelpers';
import { ValidationResult } from '../../typings/common';
import { CurrencyCode } from '../../typings/graphql-grid';
import {
    PayerData,
    PaymentMethodsData,
    WalletSwitchOffLimitEditPopupValue
} from '../../typings/walletState';
import { ButtonDecorator } from '../ButtonDecorator/ButtonDecorator';
import { ControlErrorsView } from '../ControlErrorsView/ControlErrorsView';
import { ErrorsPresenter } from '../ErrorsPresenter/ErrorsPresenter';
import { ErrorsView } from '../ErrorsView/ErrorsView';
import { ModalDecorator } from '../ModalDecorator/ModalDecorator';
import { SelectDecorator } from '../SelectDecorator/SelectDecorator';
import { Shape2, Shape2Icon } from '../Shape2/Shape2';
import { Skeleton } from '../Skeleton/Skeleton';
import { StyledLink, StyledLinkTheme } from '../StyledLink/StyledLink';
import { TextInputDecorator } from '../TextInputDecorator/TextInputDecorator';

import './WalletSwitchOffLimitEditPopup.css';
import * as keyset from './WalletSwitchOffLimitEditPopup.i18n';

const i18n = i18nFactory(keyset);
const i18nRaw = i18nRawFactory(keyset);

export interface WalletSwitchOffLimitEditPopupProps {
    isVisible: boolean;
    isSaving: boolean;
    isResetting: boolean;

    value: WalletSwitchOffLimitEditPopupValue;
    minSum: number;
    maxSum: number;
    currency: CurrencyCode;
    payerData?: Array<PayerData>;
    paymentMethods?: Array<PaymentMethodsData>;
    validation?: ValidationResult;

    isNdsActive?: boolean;

    canResetLimit: boolean;

    onValueChange: (value: WalletSwitchOffLimitEditPopupValue) => void;
    onResetButtonClick: () => void;
    onSaveButtonClick: () => void;
    onClose?: () => void;
}

export class WalletSwitchOffLimitEditPopup extends React.PureComponent<
    WalletSwitchOffLimitEditPopupProps
> {
    private ref = React.createRef<HTMLDivElement>();
    private refPayment = React.createRef<HTMLDivElement>();

    componentDidMount() {
        this.forceUpdate();
    }

    render() {
        const {
            isVisible,
            currency,
            paymentMethods,
            payerData,
            value,
            validation,
            maxSum,
            isNdsActive
        } = this.props;
        const errors = validation && validation.errors ? validation.errors : [];

        return (
            <ModalDecorator
                cls={Bem.cls('wallet-switch-off-limit-edit-popup')}
                header={i18n('header-title')}
                headerHint={
                    isNdsActive
                        ? i18n('header-hint', {
                              limit: 0,
                              currency: mapStringToCurrencySymbol(currency)
                          })
                        : i18n('header-hint-no-nds', {
                              limit: 0,
                              currency: mapStringToCurrencySymbol(currency)
                          })
                }
                footer={this.getFooter()}
                theme={'full-width-body-with-borders'}
                visible={isVisible}
                hasClose={true}
                onClose={this.close}
            >
                <ErrorsPresenter errors={errors}>
                    <div
                        className={Bem.cls(
                            'wallet-switch-off-limit-edit-popup',
                            'editor'
                        )}
                    >
                        <div
                            className={Bem.cls(
                                'wallet-switch-off-limit-edit-popup',
                                'row'
                            )}
                        >
                            <div
                                className={Bem.cls(
                                    'wallet-switch-off-limit-edit-popup',
                                    'label'
                                )}
                            >
                                {i18n('payer')}
                            </div>

                            <div
                                className={Bem.cls(
                                    'wallet-switch-off-limit-edit-popup',
                                    'editor-ctrl-wrapper'
                                )}
                                ref={this.ref}
                            >
                                {paymentMethods === undefined ||
                                payerData === undefined ? (
                                    <Skeleton height={28} isAnimating={true} />
                                ) : (
                                    <SelectDecorator
                                        cls={Bem.cls(
                                            'wallet-switch-off-limit-edit-popup',
                                            'ctrl'
                                        )}
                                        theme="normal"
                                        view={'classic'}
                                        width={'max'}
                                        size={'s'}
                                        type="radio"
                                        val={value.payerId || payerData[0].id}
                                        items={payerData.map(person => {
                                            return {
                                                val: person.id,
                                                text: person.name
                                            };
                                        })}
                                        popup={{
                                            scope: this.ref.current || undefined
                                        }}
                                        onChange={this.onPayerChange}
                                    />
                                )}

                                <ControlErrorsView
                                    path={'payerId'}
                                    cls={Bem.cls(
                                        'wallet-switch-off-limit-edit-popup',
                                        'ctrl-error'
                                    )}
                                />
                            </div>
                        </div>

                        <div
                            className={Bem.cls(
                                'wallet-switch-off-limit-edit-popup',
                                'row'
                            )}
                        >
                            <div
                                className={Bem.cls(
                                    'wallet-switch-off-limit-edit-popup',
                                    'label'
                                )}
                            >
                                {i18n('payment-method')}
                            </div>

                            <div
                                className={Bem.cls(
                                    'wallet-switch-off-limit-edit-popup',
                                    'editor-ctrl-wrapper'
                                )}
                                ref={this.refPayment}
                            >
                                {this.getPaymentMethodsControl()}

                                <ControlErrorsView
                                    path={'paymentMethodId'}
                                    cls={Bem.cls(
                                        'wallet-switch-off-limit-edit-popup',
                                        'ctrl-error'
                                    )}
                                />
                            </div>
                        </div>

                        <div
                            className={Bem.cls(
                                'wallet-switch-off-limit-edit-popup',
                                'row'
                            )}
                        >
                            <div
                                className={Bem.cls(
                                    'wallet-switch-off-limit-edit-popup',
                                    'label'
                                )}
                            >
                                {isNdsActive
                                    ? i18n('switch-off-limit', {
                                          currency: mapStringToCurrencySymbol(
                                              currency
                                          )
                                      })
                                    : i18n('switch-off-limit-no-nds-limit', {
                                          currency: mapStringToCurrencySymbol(
                                              currency
                                          )
                                      })}
                            </div>

                            <div
                                className={Bem.cls(
                                    'wallet-switch-off-limit-edit-popup',
                                    'editor-ctrl-wrapper'
                                )}
                            >
                                {paymentMethods === undefined ||
                                payerData === undefined ? (
                                    <Skeleton
                                        height={28}
                                        isAnimating={true}
                                        cls={Bem.cls(
                                            'wallet-switch-off-limit-edit-popup',
                                            'limit-value-skeleton'
                                        )}
                                    />
                                ) : (
                                    <>
                                        <TextInputDecorator
                                            cls={Bem.cls(
                                                'wallet-switch-off-limit-edit-popup',
                                                'limit-value'
                                            )}
                                            theme={'normal'}
                                            size={'s'}
                                            text={value.sum || ''}
                                            valid={this.isSumValid()}
                                            onChange={this.onSumChange}
                                            onBlur={this.formatSumValue}
                                            iconLeft={
                                                <Shape2
                                                    width={8}
                                                    height={8}
                                                    icon={Shape2Icon.minus}
                                                    cls={Bem.cls(
                                                        'wallet-switch-off-limit-edit-popup',
                                                        'limit-value-icon'
                                                    )}
                                                />
                                            }
                                        />

                                        <span
                                            className={Bem.cls(
                                                'wallet-switch-off-limit-edit-popup',
                                                'limit-value-hint'
                                            )}
                                        >
                                            {i18n('switch-off-limit-hint', {
                                                limit: this.formatMoneyValue(
                                                    maxSum
                                                ),
                                                currency: mapStringToCurrencySymbol(
                                                    currency
                                                )
                                            })}
                                        </span>
                                    </>
                                )}

                                <ControlErrorsView
                                    path={'sum'}
                                    cls={Bem.cls(
                                        'wallet-switch-off-limit-edit-popup',
                                        'ctrl-error'
                                    )}
                                />
                            </div>
                        </div>

                        <ErrorsView
                            path={'general'}
                            cls={Bem.cls(
                                'wallet-switch-off-limit-edit-popup',
                                'general-errors'
                            )}
                        />
                    </div>
                </ErrorsPresenter>
            </ModalDecorator>
        );
    }

    private getPaymentMethodsControl = () => {
        const { paymentMethods, payerData, value, currency } = this.props;

        if (paymentMethods === undefined || payerData === undefined) {
            return <Skeleton height={28} isAnimating={true} />;
        }

        if (paymentMethods && paymentMethods.length === 0) {
            return (
                <div
                    className={Bem.cls(
                        'wallet-switch-off-limit-edit-popup',
                        'no-payment-methods-hint'
                    )}
                >
                    <React.Fragment>
                        {i18nRaw('no-payment-methods', {
                            writeToUsLink: (
                                <StyledLink
                                    theme={StyledLinkTheme.blue}
                                    href={
                                        'https://yandex.ru/support/direct/payments/faq.html#other'
                                    }
                                >
                                    {i18n('write-to-us-link')}
                                </StyledLink>
                            )
                        })}
                    </React.Fragment>
                </div>
            );
        }

        const currentPaymentMethod =
            paymentMethods.find(
                method => method.id === value.paymentMethodId
            ) || paymentMethods[0];

        return (
            <SelectDecorator
                cls={Bem.cls('wallet-switch-off-limit-edit-popup', 'ctrl')}
                theme={'normal'}
                view={'classic'}
                width={'max'}
                size={'s'}
                type="radio"
                button={{
                    text: currentPaymentMethod.name,
                    view: 'classic'
                }}
                val={value.paymentMethodId || paymentMethods[0].id}
                onChange={this.onPaymentMethodChange}
                popup={{
                    scope: this.ref.current || undefined
                }}
            >
                {paymentMethods.map(({ id, name, limit }) => {
                    return (
                        <Select.Item
                            val={id}
                            key={id}
                            cls={Bem.cls(
                                'wallet-switch-off-limit-edit-popup',
                                'payment-method-menu-item'
                            )}
                        >
                            <span
                                className={Bem.cls(
                                    'wallet-switch-off-limit-edit-popup',
                                    'payment-method-item'
                                )}
                            >
                                <span
                                    className={Bem.cls(
                                        'wallet-switch-off-limit-edit-popup',
                                        'payment-method-name'
                                    )}
                                    title={name}
                                >
                                    {name}
                                </span>

                                {limit ? (
                                    <span
                                        className={Bem.cls(
                                            'wallet-switch-off-limit-edit-popup',
                                            'payment-method-limit'
                                        )}
                                    >
                                        {i18n('payment-method-limit', {
                                            value: this.formatMoneyValue(limit),
                                            currency: mapStringToCurrencySymbol(
                                                currency
                                            )
                                        })}
                                    </span>
                                ) : null}
                            </span>
                        </Select.Item>
                    );
                })}
            </SelectDecorator>
        );
    };

    private onSumChange = (newSum: string) => {
        this.props.onValueChange({
            ...this.props.value,
            sum: newSum
        });
    };

    private onPayerChange = (newPayerId: Array<string>) => {
        this.props.onValueChange({
            ...this.props.value,
            payerId: newPayerId[0]
        });
    };

    private onPaymentMethodChange = (newPaymentMethodId: Array<string>) => {
        this.props.onValueChange({
            ...this.props.value,
            paymentMethodId: newPaymentMethodId[0]
        });
    };

    private formatSumValue = () => {
        const {
            minSum,
            maxSum,
            value: { sum = '' }
        } = this.props;

        let value = parseNumber(sum);
        let formattedValueString;

        if (!isNaN(value) && sum !== '') {
            // отрицательные числа недопустимо вводить ввиде суммы порога отключения, поэтому
            // нужно автоматически преобразовывать их к положительным
            if (value < 0) {
                value = -value;
            }

            if (value < minSum) {
                value = minSum;
            }

            if (value > maxSum) {
                value = maxSum;
            }

            formattedValueString = this.formatMoneyValue(value);
        } else {
            formattedValueString = sum;
        }

        this.props.onValueChange({
            ...this.props.value,
            sum: formattedValueString
        });
    };

    private isSumValid = () => {
        const { validation } = this.props;
        const isInvalid =
            validation &&
            validation.errors &&
            validation.errors.find(error => error.path === 'sum');

        return !isInvalid;
    };

    private formatMoneyValue = (value: number) => {
        return formatNumber(value, 2);
    };

    private close = () => {
        if (this.props.onClose) {
            this.props.onClose();
        }
    };

    private getFooter = () => {
        const {
            canResetLimit,
            isSaving,
            onResetButtonClick,
            isResetting,
            payerData,
            paymentMethods
        } = this.props;

        const isLoading =
            payerData === undefined || paymentMethods === undefined;

        return (
            <>
                <div
                    className={Bem.cls(
                        'wallet-switch-off-limit-edit-popup',
                        'footer'
                    )}
                >
                    {!isLoading && (
                        <ButtonDecorator
                            view={'default'}
                            tone={'default'}
                            type={'submit'}
                            theme={'action'}
                            size={'n'}
                            text={i18n('switch-on')}
                            onClick={this.onSwitchOn}
                            progress={isSaving}
                            cls={Bem.cls(
                                'wallet-switch-off-limit-edit-popup',
                                'btn'
                            )}
                        />
                    )}

                    <ButtonDecorator
                        view={'default'}
                        theme={'pseudo'}
                        size={'n'}
                        text={i18n('cancel')}
                        onClick={this.close}
                        cls={Bem.cls(
                            'wallet-switch-off-limit-edit-popup',
                            'btn'
                        )}
                    />

                    {!isLoading && canResetLimit ? (
                        <ButtonDecorator
                            view={'default'}
                            theme={'pseudo'}
                            size={'n'}
                            text={i18n('switch-off')}
                            onClick={onResetButtonClick}
                            progress={isResetting}
                            cls={classNames(
                                Bem.cls(
                                    'wallet-switch-off-limit-edit-popup',
                                    'btn'
                                ),
                                Bem.cls(
                                    'wallet-switch-off-limit-edit-popup',
                                    'reset-btn'
                                )
                            )}
                        />
                    ) : null}
                </div>
            </>
        );
    };

    private onSwitchOn = () => {
        this.props.onSaveButtonClick();
    };
}
